import { useMemo } from 'react';
import { useRegisteredShortcuts } from '@/hooks/useHotkeys';
import { Kbd, Modal } from '@/components/ui';

/** Overlay listing every shortcut registered through `useHotkeys` (any binding that has a description). */
export function ShortcutHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
  const shortcuts = useRegisteredShortcuts();
  const groups = useMemo(() => {
    const map = new Map<string, typeof shortcuts>();
    for (const s of shortcuts) map.set(s.group, [...(map.get(s.group) ?? []), s]);
    return [...map.entries()];
  }, [shortcuts]);

  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts" size="md">
      {groups.length === 0 ? (
        <p className="text-sm text-fg-muted">No shortcuts on this screen.</p>
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map(([group, items]) => (
            <section key={group} aria-label={group}>
              <h3 className="mb-2 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
                {group}
              </h3>
              <ul className="flex flex-col gap-1.5">
                {items.map((s) => (
                  <li key={s.id} className="flex items-center justify-between gap-4 text-sm">
                    <span>{s.description}</span>
                    <Kbd keys={s.keys} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
}
